import axios, { AxiosResponse } from "axios";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import License from "../../common/models/License";
import MasterCopy, { SongDetailResponse } from "../../common/models/MasterCopy";
import BackCircle from "../../components/Buttons/BackCircle";
import SimpleButton from "../../components/Buttons/SimpleButton";
import HalfColumn from "../../components/Column/HalfColumn";
import RoundedProfilePic from "../../components/Images/RoundedProfilePic";
import SearchInput from "../../components/Inputs/SearchInput";
import { Modal } from "../../components/Modal/Modal";
import FullHeightRow from "../../components/Row/FullHeightRow";
import Row from "../../components/Row/Row";
import ScrollContainer from "../../components/Scroll/ScrollContainer";
import Text from "../../components/Text/Text";
import TextAreaWithIcon from "../../components/TextArea/TextAreaWithIcon";
import back_circle from "../../imgs/back_circle.svg";
import default_user_pic from "../../imgs/default_user_pic.svg";
import text_icon from "../imgs/text.svg";
import close_circle from "../../imgs/close-circle.svg";
import CloseCircle from "../../components/Buttons/CloseCircle";

const TitleText = styled(Text)`
  width: 100%;
  font-weight: 500;
  font-size: 28px;
  margin-right: 10px;
  margin-top: 10px;
`;
interface LicenseOwnersModalProps {
  theme: string;
}
const RightColumn = styled(HalfColumn)`
  position: relative;
  justify-content: flex-start;
  padding-left: 3%;
`;

const HalfWidthSearchInput = styled.div`
  width: 50%;
`;
const LicenseOwnersContainer = styled(ScrollContainer)`
  border-bottom: 0px solid white;
  position: absolute;
  left: 0;
  width: 48%;
`;
const LicenseOwner = styled.div`
  background: rgba(196, 196, 196, 0.15);
  margin-bottom: 3%;
  margin-left: 5%;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 10px;
  margin-right: 5%;
  border-radius: 10px;

  &:hover {
    cursor: pointer;
  }
`;

const LicenseInfo = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  right: 0;
  padding-top: 10%;
`;

const RightBackgroundOverlay = styled.div`
  position: absolute;
  background: #0e174d;
  width: 50%;
  height: 100%;
  right: 0;
  border-radius: 0px 20px 20px 0px;
`;

const RevokeLicenseContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding-right: 10%;

  @media screen and (min-height: 1080px) {
    padding-top: 10%;
  }
`;

const RevokeLicenseButton = styled(SimpleButton)`
  margin-top: 5%;
  max-width: 30%;
`;

const NumOwnersText = styled(Text)`
  margin-top: 5%;
  color: #cdcdcd;
`;

const CancelButton = styled(SimpleButton)`
  border-radius: 10px;
`;

const OwnerCard = styled.div`
  background: #000d30;
  border: 0.5px solid #ffffff90;
  border-radius: 30px;
  padding: 5%;
  margin-bottom: 5%;
`;

const LicenseBarProfilePic = styled(RoundedProfilePic)`
  width: 2rem;
  height: 2rem;
  @media screen and (max-width: 1440px) {
    width: 20px;
    height: 20px;
  }
`;

const LicenseInfoPic = styled.div<{
  bg: string;
  error?: boolean;
  outline?: boolean;
}>`
  background-image: url(${(props) => props.bg});
  width: 100px;
  height: 100px;
  -webkit-border-radius: 60px;
  -webkit-background-clip: padding-box;
  -moz-border-radius: 60px;
  -moz-background-clip: padding;
  border-radius: 60px;
  background-clip: padding-box;
  margin: 7px 0 0 5px;
  float: left;
  background-size: cover;
  background-position: center center;
  border: ${(props) =>
    props.outline
      ? props.theme.themeType === "dark"
        ? props.error
          ? "2px solid #FB4EFF"
          : "3px solid #1AFFF1"
        : "3px solid #9D7CFF"
      : ""};
`;

const LicenseOwnersModal = styled.div`
  position: relative;
  background: #000d30;
  min-width: 50%;
  min-height: 70vh;
  border-radius: 20px;
  text-align: left;
`;

export const LicenseOwners: React.FC<LicenseOwnersModalProps> = ({ theme }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const song_id = useParams();
  const [licenses, setLicense] = useState<License[]>();
  const [searchQuery, setSearchQuery] = useState("");
  const [currentLicenseIndex, setCurrentLicenseIndex] = useState(-1);
  const [revokeLicenseClicked, setRevokeLicenseClicked] = useState(false);
  const [description, setDescription] = useState("");
  const ref = useRef();

  useEffect(() => {
    axios
      .get<SongDetailResponse>(
        process.env.REACT_APP_BACKEND_URL +
          "/get_song_details?id=" +
          song_id.songid
      )
      .then((response: AxiosResponse<SongDetailResponse>) => {
        const song = response.data.masterCopyDetails as MasterCopy;
        getLicenseOwners(song);
      });
  }, [song_id]);

  const getLicenseOwners = async (song: MasterCopy) => {
    axios
      .get(
        process.env.REACT_APP_BACKEND_URL + "/get_licenses_from_address?license_address=" + song.license_address
      )
      .then((response: AxiosResponse) => {
        const licenses = response.data.licenses as Array<License>;
        setLicense(licenses);
      });
  };

  const onRevokeLicensesClick = () => {
    setRevokeLicenseClicked(true);
  };

  const timeConverter = (unixTimestamp: number) => {
    let a = new Date(unixTimestamp * 1000);
    return a.toString();
  };

  const onBackClick = () => {
    const previousLocation = location.pathname.replace("license_owners", "");
    navigate(previousLocation);
  };

  const onLicenseOwnerClick = async (licenseIndex: number) => {
    setCurrentLicenseIndex(licenseIndex);
  };

  const onDescriptionChange = (
    event: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    setDescription(event.target.value);
  };

  const onCancelClick = () => {
    setRevokeLicenseClicked(false);
  };

  const onConfirmClick = () => {
    const jsonData = {
      revoke_description: description,
      license_id: licenses[currentLicenseIndex].id,
    };
    axios
      .post(
        process.env.REACT_APP_BACKEND_URL + "/set_pending_revocation",
        JSON.stringify(jsonData),
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      )
      .then((response: AxiosResponse) => {
        const revoked_license = response.data.revoked_license as License;
        licenses[currentLicenseIndex] = revoked_license;
        setRevokeLicenseClicked(false);
      });
  };

  const search = (licenses: Array<License>) => {
    return licenses.filter((license) =>
      license.owner
        .toString()
        .toLowerCase()
        .includes(searchQuery.toLowerCase().trim())
    );
  };

  const onCloseClick = () => {
    navigate("..");
  };

  return (
    <>
      <Modal ref={ref} show={true}>
        <LicenseOwnersModal>
          <BackCircle bg={back_circle} onClick={onBackClick} />
          <CloseCircle onClick={onCloseClick} bg={close_circle} />
          <FullHeightRow style={{ alignItems: "flex-start", marginLeft: "3%" }}>
            <HalfColumn style={{ marginTop: "10%" }}>
              <TitleText>License Owners</TitleText>
              <HalfWidthSearchInput>
                <SearchInput
                  onTextChange={(text: string) => {
                    setSearchQuery(text);
                  }}
                  theme={theme}
                />
              </HalfWidthSearchInput>
              {licenses && (
                <div>
                  <NumOwnersText>
                    Total: {search(licenses).length}{" "}
                    {licenses.length > 1 ? "owners" : "owner"}{" "}
                  </NumOwnersText>

                  <LicenseOwnersContainer>
                    {licenses.length > 0 &&
                      search(licenses).map((license, index) => (
                        <LicenseOwner
                          key={license.id}
                          onClick={() => {
                            onLicenseOwnerClick(index);
                          }}
                        >
                          {/* <LicenseBarProfilePic
                            bg={default_user_pic}
                            outline={false}
                          /> */}
                          <img src={default_user_pic} width={'30px'} height={'30px'} style={{marginLeft: '10px'}}/>
                          <Text>{license.owner}</Text>
                        </LicenseOwner>
                      ))}

                  </LicenseOwnersContainer>
                </div>
              )}
            </HalfColumn>
            <RightBackgroundOverlay />

            <RightColumn>
              {currentLicenseIndex > -1 &&
                licenses[currentLicenseIndex].id !== "" &&
                (!revokeLicenseClicked ? (
                  <LicenseInfo>
                    <LicenseInfoPic bg={default_user_pic} outline={false} />
                    <Text>{licenses[currentLicenseIndex].owner}</Text>
                    <Text>License ID: {licenses[currentLicenseIndex].id}</Text>
                    <Text>License Status: Active</Text>
                    <Text>License Type: Youtube</Text>
                    <Text>
                      Active Date:{" "}
                      {timeConverter(
                        parseInt(licenses[currentLicenseIndex].expiration) -
                          31536000
                      )}
                    </Text>
                    <Text>
                      Expiration Date:{" "}
                      {timeConverter(
                        parseInt(licenses[currentLicenseIndex].expiration)
                      )}
                    </Text>
                    {!licenses[currentLicenseIndex].pending_revocation ? (
                      licenses[currentLicenseIndex].revoked ? (
                        <RevokeLicenseButton disabled>
                          Revoked
                        </RevokeLicenseButton>
                      ) : (
                        <RevokeLicenseButton onClick={onRevokeLicensesClick}>
                          Revoke License
                        </RevokeLicenseButton>
                      )
                    ) : (
                      <>
                        <Text>
                          Revoke Reason:{" "}
                          {licenses[currentLicenseIndex].revoke_comments}
                        </Text>
                        <SimpleButton
                          disabled={true}
                          style={{ width: "50%", marginTop: "5%" }}
                        >
                          Pending Revocation
                        </SimpleButton>
                      </>
                    )}
                  </LicenseInfo>
                ) : (
                  <RevokeLicenseContainer>
                    <Text bold={true} size={"24px"}>
                      Revoke License
                    </Text>
                    <OwnerCard>
                      <Row align="flex-start" gap="10px">
                        <LicenseBarProfilePic
                          bg={default_user_pic}
                          outline={false}
                        />
                        <Text
                          style={{ maxWidth: "90%", wordWrap: "break-word" }}
                        >
                          {licenses[currentLicenseIndex].owner}
                        </Text>
                      </Row>
                      <Text style={{ marginTop: "5%" }}>
                        License ID: {licenses[currentLicenseIndex].id}
                      </Text>
                    </OwnerCard>
                    <Text>
                      Could you write a few words to explain the reason you are
                      revoking?
                    </Text>
                    <TextAreaWithIcon
                      rows={8}
                      cols={8}
                      src={text_icon}
                      onChange={onDescriptionChange}
                      defaultValue={description}
                      placeholder="enter your comments here"
                    />
                    <Row
                      style={{
                        justifyContent: "space-between",
                        marginTop: "10%",
                      }}
                    >
                      <CancelButton secondary onClick={onCancelClick}>
                        Cancel
                      </CancelButton>
                      <SimpleButton onClick={onConfirmClick}>
                        Confirm
                      </SimpleButton>
                    </Row>
                  </RevokeLicenseContainer>
                ))}
            </RightColumn>
          </FullHeightRow>
        </LicenseOwnersModal>
      </Modal>
    </>
  );
};
